import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Badge, eur, dt } from "../components/common";
import { toast } from "sonner";
import { useAutoRefresh } from "../lib/autoRefresh";

const NEXT = {
  rascunho: ["emitida"],
  emitida: ["paga", "vencida", "anulada"],
  vencida: ["paga", "anulada"],
};

async function openInvoicePdf(id) {
  try {
    const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
  }
}

function MBRef({ r }) {
  if (!r.mb_entidade || !r.mb_referencia) {
    return <span className="text-xs text-slate-400">-</span>;
  }
  return (
    <div className="text-xs leading-tight" data-testid={`inv-${r.id}-mb`}>
      <div>Ent. <code className="bg-tint px-1 rounded">{r.mb_entidade}</code></div>
      <div>Ref. <code className="bg-tint px-1 rounded">{r.mb_referencia}</code></div>
      {r.mb_valor != null && <div className="text-slate-500">{eur(r.mb_valor)}</div>}
    </div>
  );
}

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/invoices").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const transition = async (id, estado) => {
    try {
      await api.post(`/invoices/${id}/transition?estado=${estado}`);
      toast.success(`Fatura -> ${estado}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const refresh = async (id) => {
    setBusy(id);
    try {
      await api.post(`/moloni/invoices/${id}/refresh`);
      toast.success("Atualizado a partir do Moloni");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const genMB = async (id) => {
    setBusy(id);
    try {
      await api.post(`/moloni/invoices/${id}/mb-reference`);
      toast.success("Referencia Multibanco gerada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const syncAll = async () => {
    setBusy("all");
    try {
      const r = await api.post(`/moloni/sync-invoices`);
      toast.success(`Reconciliadas ${r.data.reconciliadas}/${r.data.total}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const cols = [
    { key: "moloni_document_id", label: "Doc Moloni", render: (r) => <code className="text-xs bg-tint px-2 py-0.5 rounded">{r.moloni_document_id || "-"}</code> },
    { key: "organizacao_nome", label: "Cliente", render: (r) => (
      <div className="leading-tight">
        <div className="font-semibold text-navy">{r.organizacao_nome}</div>
        {r.partner_id && r.subscritor_original && r.subscritor_original !== r.organizacao_nome && (
          <div className="text-[10px] text-blue" data-testid={`inv-${r.id}-partner-badge`}>
            via parceiro - servico a: {r.subscritor_original}
          </div>
        )}
      </div>
    ) },
    { key: "mb", label: "Ref. Multibanco", render: (r) => <MBRef r={r} /> },
    { key: "total", label: "Total", render: (r) => eur(r.total ?? r.valor_total) },
    { key: "data_vencimento", label: "Vencimento", render: (r) => dt(r.data_vencimento) },
    { key: "estado", label: "Estado", render: (r) => <Badge value={r.estado} /> },
    {
      key: "docs",
      label: "Documentos",
      render: (r) => (
        <div className="flex flex-col gap-0.5 text-xs">
          <button onClick={() => openInvoicePdf(r.id)} data-testid={`inv-${r.id}-pdf-cog`}
            className="text-left text-blue hover:underline">
            PDF Cogniseg
          </button>
          {r.moloni_pdf_url && <a href={r.moloni_pdf_url} target="_blank" rel="noreferrer" data-testid={`inv-${r.id}-pdf`} className="text-blue hover:underline">Fatura Moloni</a>}
          {r.recibo_pdf_url && <a href={r.recibo_pdf_url} target="_blank" rel="noreferrer" data-testid={`inv-${r.id}-recibo`} className="text-green-600 hover:underline">Recibo PDF</a>}
        </div>
      ),
    },
    {
      key: "acoes",
      label: "Acoes",
      render: (r) => (
        <div className="flex gap-1 flex-wrap">
          {(NEXT[r.estado] || []).map((s) => (
            <button key={s} onClick={() => transition(r.id, s)} data-testid={`inv-${r.id}-${s}`}
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white transition-colors">{s}</button>
          ))}
          {r.moloni_document_id && (
            <>
              <button disabled={busy === r.id} onClick={() => refresh(r.id)} data-testid={`inv-${r.id}-refresh`}
                className="text-xs px-2 py-1 rounded bg-navy text-white hover:bg-blue disabled:opacity-50 transition-colors">Atualizar Moloni</button>
              {!r.mb_referencia && (
                <button disabled={busy === r.id} onClick={() => genMB(r.id)} data-testid={`inv-${r.id}-genmb`}
                  className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white disabled:opacity-50 transition-colors">Gerar Ref. MB</button>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader overline="Faturacao Moloni" title="Faturacao"
        action={
          <button onClick={syncAll} disabled={busy === "all"} data-testid="invoices-sync-all"
            className="text-sm px-4 py-2 rounded-lg bg-navy text-white hover:bg-blue disabled:opacity-50 transition-colors">
            {busy === "all" ? "A reconciliar..." : "Reconciliar Moloni"}
          </button>
        } />
      <Table columns={cols} rows={rows} testid="invoices-table" />
    </div>
  );
}
